import { API_URL, formatApiError } from '../utils/api';
import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Buildings,
  Plus,
  MagnifyingGlass,
  PencilSimple,
  Trash,
  Globe,
  Clock,
  Phone,
  EnvelopeSimple,
  MapPin,
  GlobeSimple,
  CurrencyDollar,
  ArrowsClockwise
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { DataToolbar } from '../components/DataToolbar';
import { DataTable } from '../components/DataTable';

// API_URL imported from utils

const ENTITY_TYPES = [
  { value: 'Subsidiary', label: 'Subsidiary' },
  { value: 'Licenced', label: 'Licenced' },
];

const initialFormData = {
  entity_code: '',
  entity_type: 'Subsidiary',
  entity_name: '',
  country_code: '',
  currency_code: '',
  timezone: '',
  address_line_1: '',
  address_line_2: '',
  address_line_3: '',
  address_line_4: '',
  address_line_5: '',
  physical_address: '',
  telephone_no: '',
  email: '',
  website: '',
  customer_care_1: '',
  customer_care_2: '',
  default_sub_fee: '0',
  ic_card_pid: '',
};

export default function EntitiesPage() {
  const { isSuperAdmin, user } = useAuth();
  const { config, fetchEntities: refreshEntities } = useTenant();
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEntity, setEditingEntity] = useState(null);
  const [formData, setFormData] = useState({ ...initialFormData });

  const availableCurrencies = useMemo(() => {
    const currencyMap = new Map();
    config.countries.forEach(country => {
      if (country.currency_code && !currencyMap.has(country.currency_code)) {
        currencyMap.set(country.currency_code, {
          code: country.currency_code,
          country: country.name
        });
      }
    });
    return Array.from(currencyMap.values());
  }, [config.countries]);

  const availableTimezones = useMemo(() => {
    const tzMap = new Map();
    config.countries.forEach(country => {
      if (country.timezone && !tzMap.has(country.timezone)) {
        tzMap.set(country.timezone, {
          value: country.timezone,
          country: country.name
        });
      }
    });
    return Array.from(tzMap.values());
  }, [config.countries]);

  const fetchNextCode = useCallback(async (countryCode, entityType) => {
    if (!countryCode || !entityType) return;
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { data } = await axios.get(
        `${API_URL}/api/entities/next-code?country_code=${countryCode}&entity_type=${entityType}`,
        { withCredentials: true, headers }
      );
      setFormData(prev => ({ ...prev, entity_code: data.entity_code }));
    } catch (error) {
      console.error('Error fetching next code:', error);
    }
  }, []);

  const handleCountryChange = (countryCode) => {
    const selectedCountry = config.countries.find(c => c.code === countryCode);
    setFormData(prev => {
      const updated = {
        ...prev,
        country_code: countryCode,
        currency_code: selectedCountry?.currency_code || prev.currency_code,
        timezone: selectedCountry?.timezone || prev.timezone
      };
      return updated;
    });
    if (!editingEntity) {
      fetchNextCode(countryCode, formData.entity_type);
    }
  };

  const handleEntityTypeChange = (entityType) => {
    setFormData(prev => ({ ...prev, entity_type: entityType }));
    if (!editingEntity && formData.country_code) {
      fetchNextCode(formData.country_code, entityType);
    }
  };

  const fetchEntities = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { data } = await axios.get(`${API_URL}/api/entities`, {
        withCredentials: true,
        headers
      });
      setEntities(data);
    } catch (error) {
      console.error('Error fetching entities:', error);
      toast.error('Failed to fetch entities');
    } finally {
      setLoading(false);
    }
  }, []);

  const syncFromMiddleware = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.get(`${API_URL}/api/entities/sync-middleware`, { withCredentials: true, headers });
      await fetchEntities();
    } catch (e) { /* silent */ }
  }, [fetchEntities]);

  useEffect(() => {
    fetchEntities();
    syncFromMiddleware();
  }, [fetchEntities, syncFromMiddleware]);

  const resetForm = () => {
    setFormData({ ...initialFormData });
    setEditingEntity(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const openEditDialog = (entity) => {
    setEditingEntity(entity);
    setFormData({
      entity_code: entity.entity_code || '',
      entity_type: entity.entity_type || 'Subsidiary',
      entity_name: entity.entity_name || '',
      country_code: entity.country_code || '',
      currency_code: entity.currency_code || '',
      timezone: entity.timezone || '',
      address_line_1: entity.address_line_1 || '',
      address_line_2: entity.address_line_2 || '',
      address_line_3: entity.address_line_3 || '',
      address_line_4: entity.address_line_4 || '',
      address_line_5: entity.address_line_5 || '',
      physical_address: entity.physical_address || '',
      telephone_no: entity.telephone_no || '',
      email: entity.email || '',
      website: entity.website || '',
      customer_care_1: entity.customer_care_1 || '',
      customer_care_2: entity.customer_care_2 || '',
      default_sub_fee: entity.default_sub_fee || '0',
      ic_card_pid: entity.ic_card_pid || '',
    });
    setIsDialogOpen(true);
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      if (editingEntity) {
        await axios.put(`${API_URL}/api/entities/${editingEntity.id}`, formData, {
          withCredentials: true,
          headers
        });
        toast.success('Entity updated successfully');
      } else {
        await axios.post(`${API_URL}/api/entities`, formData, {
          withCredentials: true,
          headers
        });
        toast.success('Entity created successfully');
      }
      
      setIsDialogOpen(false);
      resetForm();
      fetchEntities();
      refreshEntities();
    } catch (error) {
      const message = formatApiError(error, 'Failed to save entity');
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (entity) => {
    if (!window.confirm(`Are you sure you want to delete ${entity.entity_name}?`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API_URL}/api/entities/${entity.id}`, {
        withCredentials: true,
        headers
      });
      toast.success('Entity deleted successfully');
      fetchEntities();
      refreshEntities();
    } catch (error) {
      toast.error('Failed to delete entity');
    }
  };

  const filteredEntities = entities.filter(entity =>
    entity.entity_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    entity.entity_code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isSuperAdmin) {
    return (
      <div className="bg-[#FFFFFF] border border-[#EDE6D8] rounded-lg p-8 text-center">
        <p className="text-[#78716C]">Access denied. Super admin privileges required.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3 pb-6" data-testid="entities-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-[#1C1917] tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          Entities
        </h1>
        <div className="flex gap-2">
          <Button
            onClick={openCreateDialog}
            className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg"
            data-testid="create-entity-btn"
          >
            <Plus size={18} className="mr-2" />
            Add Entity
          </Button>
        </div>
      </div>

      {/* Toolbar */}
      <DataToolbar
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder="Search by name or code…"
        testId="entities-toolbar"
        onClearAll={() => setSearchQuery('')}
      />

      <DataTable
        columns={[
          { key: 'entity', label: 'Entity', width: 'minmax(220px,2fr)', render: (e) => (
            <span className="flex items-center gap-3 min-w-0">
              <span className="shrink-0 w-8 h-8 rounded-full bg-[#0A2540]/10 group-hover:bg-[#EDE6D8] flex items-center justify-center">
                <span className="text-[10px] font-bold text-[#0A2540] group-hover:text-[#1C1917]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  {(e.entity_name || '?').split(/\s+/).slice(0,2).map(s => s[0]).join('').toUpperCase()}
                </span>
              </span>
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold text-[#1C1917] group-hover:text-[#1C1917]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  {e.entity_name}
                </span>
                <span className="block truncate text-[10px] text-[#78716C] group-hover:text-[#78716C] font-mono">
                  {e.entity_code}
                </span>
              </span>
            </span>
          ) },
          { key: 'country', label: 'Country', width: 'minmax(110px,0.9fr)', mono: true, tone: 'muted',
            render: (e) => `${e.country_code || ''}${e.currency_code ? ' · ' + e.currency_code : ''}` || '—' },
          { key: 'campuses_count', label: 'Campuses', width: 'minmax(90px,0.7fr)', mono: true, align: 'right', tone: 'strong',
            render: (e) => Number(e.campuses_count || 0).toLocaleString() },
          { key: 'stores_count', label: 'Stores',   width: 'minmax(80px,0.6fr)', mono: true, align: 'right', tone: 'strong',
            render: (e) => Number(e.stores_count || 0).toLocaleString() },
          { key: 'users_count',  label: 'Users',    width: 'minmax(80px,0.6fr)', mono: true, align: 'right', tone: 'strong',
            render: (e) => Number(e.users_count || 0).toLocaleString() },
          { key: 'status', label: 'Status', width: 'minmax(100px,0.9fr)', render: (e) => (
            <span className="flex items-center gap-1.5">
              <span className={`w-1.5 h-1.5 rounded-full ${e.status === 'active' ? 'bg-emerald-500' : 'bg-rose-500'} group-hover:bg-[#FFFFFF]`} />
              <span className={`text-xs font-medium ${e.status === 'active' ? 'text-emerald-700' : 'text-rose-700'} group-hover:text-[#1C1917]`} style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                {e.status || '—'}
              </span>
            </span>
          ) },
        ]}
        rows={filteredEntities}
        loading={loading}
        rowKey={(e) => e.id}
        onRowClick={(e) => openEditDialog(e)}
        emptyIcon={<Buildings size={20} className="text-slate-500" />}
        emptyTitle="No entities yet"
        emptyText={searchQuery ? 'Try clearing your search.' : 'Add an entity or sync from middleware.'}
        testIdPrefix="entity"
        actionRender={(e) => (
          <span className="flex items-center justify-end gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={(ev) => { ev.stopPropagation(); openEditDialog(e); }}
              className="text-[#0A2540] hover:text-[#0A2540] hover:bg-[#0A2540]/10 group-hover:text-[#1C1917] group-hover:bg-[#EDE6D8] h-8 w-8 p-0"
              data-testid={`edit-entity-${e.id}`}
            >
              <PencilSimple size={14} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={(ev) => { ev.stopPropagation(); handleDelete(e); }}
              className="text-[#EF4444] hover:text-[#EF4444] hover:bg-[#EF4444]/10 group-hover:text-[#1C1917] group-hover:bg-[#EDE6D8] h-8 w-8 p-0"
              data-testid={`delete-entity-${e.id}`}
            >
              <Trash size={14} />
            </Button>
          </span>
        )}
      />

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {editingEntity ? 'Edit Entity' : 'Create Entity'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Section: Basic Information */}
            <div>
              <h3 className="text-sm font-medium text-[#0A2540] mb-3 flex items-center gap-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                <Buildings size={16} />
                Basic Information
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Entity Code <span className="text-[#EF4444]">*</span></Label>
                  <Input
                    value={formData.entity_code}
                    onChange={(e) => updateField('entity_code', e.target.value)}
                    placeholder="Auto-generated"
                    className="border-[#EDE6D8] rounded-lg bg-[#FCFAF6]"
                    readOnly
                    data-testid="entity-code-input"
                  />
                  {!editingEntity && (
                    <p className="text-xs text-[#78716C]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      Select Country & Type to generate
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Entity Name <span className="text-[#EF4444]">*</span></Label>
                  <Input
                    value={formData.entity_name}
                    onChange={(e) => updateField('entity_name', e.target.value)}
                    placeholder="e.g., Ecowater Tanzania"
                    className="border-[#EDE6D8] rounded-lg"
                    required
                    data-testid="entity-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Entity Type <span className="text-[#EF4444]">*</span></Label>
                  <Select
                    value={formData.entity_type}
                    onValueChange={handleEntityTypeChange}
                  >
                    <SelectTrigger className="border-[#EDE6D8] rounded-lg" data-testid="entity-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {ENTITY_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Section: Location */}
            <div>
              <h3 className="text-sm font-medium text-[#0A2540] mb-3 flex items-center gap-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                <Globe size={16} />
                Location
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Country <span className="text-[#EF4444]">*</span></Label>
                  <Select
                    value={formData.country_code}
                    onValueChange={handleCountryChange}
                  >
                    <SelectTrigger className="border-[#EDE6D8] rounded-lg" data-testid="country-select">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {config.countries.map((country) => (
                        <SelectItem key={country.code} value={country.code}>
                          {country.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Currency</Label>
                  <Select
                    value={formData.currency_code}
                    onValueChange={(value) => updateField('currency_code', value)}
                  >
                    <SelectTrigger className="border-[#EDE6D8] rounded-lg" data-testid="currency-select">
                      <SelectValue placeholder="Select currency" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableCurrencies.map((currency) => (
                        <SelectItem key={currency.code} value={currency.code}>
                          {currency.code}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Timezone</Label>
                  <Select
                    value={formData.timezone}
                    onValueChange={(value) => updateField('timezone', value)}
                  >
                    <SelectTrigger className="border-[#EDE6D8] rounded-lg" data-testid="timezone-select">
                      <SelectValue placeholder="Select timezone" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableTimezones.map((tz) => (
                        <SelectItem key={tz.value} value={tz.value}>
                          {tz.value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Section: Address */}
            <div>
              <h3 className="text-sm font-medium text-[#0A2540] mb-3 flex items-center gap-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                <MapPin size={16} />
                Address
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Address Line 1 <span className="text-[#EF4444]">*</span></Label>
                  <Input
                    value={formData.address_line_1}
                    onChange={(e) => updateField('address_line_1', e.target.value)}
                    placeholder="e.g., P.O Box 12345"
                    className="border-[#EDE6D8] rounded-lg"
                    required
                    data-testid="address-line-1-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Address Line 2 <span className="text-[#EF4444]">*</span></Label>
                  <Input
                    value={formData.address_line_2}
                    onChange={(e) => updateField('address_line_2', e.target.value)}
                    placeholder="e.g., Dodoma, Tanzania"
                    className="border-[#EDE6D8] rounded-lg"
                    required
                    data-testid="address-line-2-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Address Line 3</Label>
                  <Input
                    value={formData.address_line_3}
                    onChange={(e) => updateField('address_line_3', e.target.value)}
                    placeholder="Optional"
                    className="border-[#EDE6D8] rounded-lg"
                    data-testid="address-line-3-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Physical Address <span className="text-[#EF4444]">*</span></Label>
                  <Input
                    value={formData.physical_address}
                    onChange={(e) => updateField('physical_address', e.target.value)}
                    placeholder="e.g., Kisasa South Kanisani"
                    className="border-[#EDE6D8] rounded-lg"
                    required
                    data-testid="physical-address-input"
                  />
                </div>
              </div>
            </div>

            {/* Section: Contact Information */}
            <div>
              <h3 className="text-sm font-medium text-[#0A2540] mb-3 flex items-center gap-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                <Phone size={16} />
                Contact Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Telephone <span className="text-[#EF4444]">*</span></Label>
                  <Input
                    value={formData.telephone_no}
                    onChange={(e) => updateField('telephone_no', e.target.value)}
                    placeholder="e.g., 255767821881"
                    className="border-[#EDE6D8] rounded-lg"
                    required
                    data-testid="telephone-no-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email <span className="text-[#EF4444]">*</span></Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => updateField('email', e.target.value)}
                    placeholder="e.g., test@ecowater-tz.com"
                    className="border-[#EDE6D8] rounded-lg"
                    required
                    data-testid="entity-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Website</Label>
                  <Input
                    value={formData.website}
                    onChange={(e) => updateField('website', e.target.value)}
                    placeholder="e.g., www.ecowater-tz.com"
                    className="border-[#EDE6D8] rounded-lg"
                    data-testid="entity-website-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Customer Care 1</Label>
                  <Input
                    value={formData.customer_care_1}
                    onChange={(e) => updateField('customer_care_1', e.target.value)}
                    placeholder="e.g., 255767123123"
                    className="border-[#EDE6D8] rounded-lg"
                    data-testid="customer-care-1-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Customer Care 2</Label>
                  <Input
                    value={formData.customer_care_2}
                    onChange={(e) => updateField('customer_care_2', e.target.value)}
                    placeholder="e.g., 255767123124"
                    className="border-[#EDE6D8] rounded-lg"
                    data-testid="customer-care-2-input"
                  />
                </div>
              </div>
            </div>

            {/* Section: Configuration */}
            <div>
              <h3 className="text-sm font-medium text-[#0A2540] mb-3 flex items-center gap-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                <CurrencyDollar size={16} />
                Configuration
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Default Subscription Fee</Label>
                  <Input
                    value={formData.default_sub_fee}
                    onChange={(e) => updateField('default_sub_fee', e.target.value)}
                    placeholder="e.g., 5000"
                    className="border-[#EDE6D8] rounded-lg"
                    data-testid="default-sub-fee-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>IC Card PID <span className="text-[#EF4444]">*</span></Label>
                  <Input
                    value={formData.ic_card_pid}
                    onChange={(e) => updateField('ic_card_pid', e.target.value)}
                    placeholder="e.g., 1299"
                    className="border-[#EDE6D8] rounded-lg"
                    required
                    data-testid="ic-card-pid-input"
                  />
                </div>
              </div>
            </div>

            <DialogFooter className="pt-4 border-t border-[#EDE6D8]">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                className="border-[#EDE6D8] rounded-lg"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg"
                data-testid="save-entity-btn"
              >
                {submitting ? 'Saving...' : (editingEntity ? 'Update' : 'Create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
